import * as p from "@clack/prompts";
import pc from "picocolors";
import { clampUsagePeriod, lastNDaysPeriod, OCS_MAX_USAGE_WINDOW_DAYS } from "@carrier/ocs-client";
import { callMcpTool } from "./ask.js";
import { callRestEndpoint } from "./rest.js";
import { withNextStep } from "./guidance.js";
import { capabilityTarget } from "./domains.js";
import type { CapabilityCommand, CapabilityDomain, CapabilityOption } from "./domains.js";

/** Flags commander adds to every capability command on top of the registry. */
export interface CapabilityFlags {
  json?: boolean;
  commit?: boolean;
  /** Second-call token for REST endpoints that answered 202. */
  confirmToken?: string;
}

/** Longest printable body before we tell the reader to use --json. */
const MAX_PRETTY_CHARS = 8000;

/**
 * The property commander stores an option under: the long flag, camel-cased.
 * "--threshold-pct <n>" → "thresholdPct".
 */
export function optionKey(flags: string): string {
  const long = flags.split(/[ ,|]+/).find((t) => t.startsWith("--"));
  if (!long) throw new Error(`Option "${flags}" has no long flag`);
  return long
    .replace(/^--/, "")
    .split("-")
    .map((part, i) => (i === 0 ? part : part.charAt(0).toUpperCase() + part.slice(1)))
    .join("");
}

/** Write `value` at a dot path, creating the intermediate objects. */
function setArg(target: Record<string, unknown>, path: string, value: unknown): void {
  const parts = path.split(".");
  let node = target;
  for (const part of parts.slice(0, -1)) {
    const next = node[part];
    if (typeof next !== "object" || next === null) node[part] = {};
    node = node[part] as Record<string, unknown>;
  }
  node[parts[parts.length - 1]!] = value;
}

function coerce(opt: CapabilityOption, raw: unknown): { value?: unknown; error?: string } {
  if (opt.type === "boolean") {
    // Plain flags (no value) parse to `true`; value-taking boolean flags like
    // "--data <bool>" carry an explicit "true"/"false" so tri-state tools can
    // tell "set to false" apart from "leave unchanged".
    if (raw === true) return { value: true };
    const text = String(raw).toLowerCase();
    if (text === "true") return { value: true };
    if (text === "false") return { value: false };
    return { error: `${opt.flags} expects true or false, got "${String(raw)}"` };
  }
  if (opt.type === "number") {
    const n = Number(raw);
    if (!Number.isFinite(n)) return { error: `${opt.flags} expects a number, got "${String(raw)}"` };
    return { value: n };
  }
  if (opt.type === "string[]" || opt.type === "number[]") {
    // One flag, commas inside. A list tool argument has no other shape a shell
    // can pass without quoting rules nobody remembers.
    const items = String(raw)
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    if (!items.length) return { error: `${opt.flags} expects a comma-separated list, got "${String(raw)}"` };
    if (opt.type === "string[]") return { value: items };
    const numbers = items.map(Number);
    const bad = items.find((_, i) => !Number.isFinite(numbers[i]!));
    if (bad !== undefined) return { error: `${opt.flags} expects numbers, got "${bad}"` };
    return { value: numbers };
  }
  return { value: String(raw) };
}

export interface BuiltArgs {
  args: Record<string, unknown>;
  errors: string[];
  notes: string[];
}

/** Map parsed commander options onto the tool's argument shape. */
export function buildToolArgs(
  cmd: CapabilityCommand,
  opts: Record<string, unknown>,
): BuiltArgs {
  const args: Record<string, unknown> = {};
  const errors: string[] = [];
  const notes: string[] = [];

  for (const opt of cmd.options) {
    const raw = opts[optionKey(opt.flags)];
    if (raw === undefined || raw === false) {
      if (opt.required) errors.push(`Missing required ${opt.flags}`);
      continue;
    }
    const { value, error } = coerce(opt, raw);
    if (error) errors.push(error);
    else setArg(args, opt.arg, value);
  }

  if (cmd.requireOneOf && !cmd.requireOneOf.some((name) => args[name] !== undefined)) {
    const flags = cmd.options.filter((opt) => cmd.requireOneOf!.includes(opt.arg)).map((opt) => opt.flags);
    errors.push(`Pass one of: ${flags.join(", ")}`);
  }

  if (cmd.usageWindow) {
    const start = args.startDate as string | undefined;
    const end = args.endDate as string | undefined;
    if (!start && !end) {
      const window = lastNDaysPeriod();
      args.startDate = window.start;
      args.endDate = window.end;
      notes.push(`Window defaulted to ${window.start} … ${window.end} (last ${OCS_MAX_USAGE_WINDOW_DAYS} days).`);
    } else if (!start || !end) {
      errors.push("Pass both --start and --end, or neither for the last 7 days");
    } else {
      const clamped = clampUsagePeriod(start, end);
      if (clamped.start !== start) {
        notes.push(
          `Start moved to ${clamped.start} — OCS rejects windows wider than ${OCS_MAX_USAGE_WINDOW_DAYS} days.`,
        );
      }
      args.startDate = clamped.start;
      args.endDate = clamped.end;
    }
  }

  // dry_run is an MCP tool argument. REST endpoints have no such field, so
  // sending it would either 400 or be silently ignored — on a write, silently
  // ignored means the change lands. There, --commit is the gate instead.
  if (cmd.write && cmd.transport !== "rest") args.dry_run = true;
  return { args, errors, notes };
}

/**
 * Keep already-structured tool responses unchanged, but make text-only MCP
 * successes valid JSON so every --json stdout value can be consumed by jq.
 */
export function formatJsonOutput(text: string): string {
  try {
    JSON.parse(text);
    return text;
  } catch {
    return JSON.stringify(text);
  }
}

/**
 * Run one domain subcommand against whichever transport it names.
 *
 * Write commands preview unless --commit is passed, so a mistyped ICCID costs
 * nothing: MCP tools by sending dry_run, REST endpoints by printing the request
 * without sending it.
 */
export async function runCapability(
  domain: CapabilityDomain,
  cmd: CapabilityCommand,
  opts: Record<string, unknown> & CapabilityFlags,
): Promise<void> {
  const label = `${domain.name} ${cmd.name}`;
  // --json keeps stdout pipeable: diagnostics go to stderr, no spinner, no frame.
  const quiet = opts.json === true;
  const { args, errors, notes } = buildToolArgs(cmd, opts);

  if (cmd.write && opts.commit === true) delete args.dry_run;

  // Tools with no dry_run, confirm or confirm_token of their own do the thing on
  // the first call. --confirm is read straight off `opts` rather than through
  // CapabilityFlags, because a tool with a real `confirm` argument registers the
  // same flag as a value-taking option; `requireConfirm` and that argument are
  // mutually exclusive, and the generator refuses to emit both.
  if (cmd.requireConfirm && opts.confirm !== true) {
    errors.push(`${capabilityTarget(cmd)} has no preview mode. Pass --confirm to run it for real.`);
  }

  if (errors.length) {
    const message = withNextStep(`Cannot run \`carrier ${label}\`.`, [
      ...errors,
      `See every flag: carrier ${label} --help`,
    ]);
    if (quiet) console.error(message);
    else p.log.error(message);
    process.exitCode = 1;
    return;
  }

  if (cmd.transport === "rest" && cmd.write && opts.commit !== true) {
    const preview = [
      `${cmd.restMethod} ${cmd.restPath}`,
      JSON.stringify(args, null, 2),
      "",
      "Dry run — nothing sent. Re-run with --commit to apply.",
    ].join("\n");
    if (quiet) process.stdout.write(`${formatJsonOutput(preview)}\n`);
    else p.note(preview, `carrier ${label}`);
    return;
  }

  const dryRun = cmd.write === true && args.dry_run === true;
  const warnings = dryRun
    ? [...notes, "Dry run — nothing changes. Re-run with --commit to apply."]
    : notes;
  for (const note of warnings) {
    if (quiet) console.error(note);
    else p.log.info(pc.dim(note));
  }

  const target = capabilityTarget(cmd);
  const errorHints = [
    `Check the arguments: carrier ${label} --help`,
    'Or describe the task in words: carrier ask "…"',
  ];

  const s = quiet ? null : p.spinner();
  s?.start(`Calling ${target}`);
  const result =
    cmd.transport === "rest"
      ? await callRestEndpoint(cmd.restMethod, cmd.restPath, args, {
          errorHints,
          confirmToken: opts.confirmToken,
        })
      : await callMcpTool(cmd.tool, args, { errorHints });
  s?.stop(result.ok ? "Done" : "Failed");

  if (!result.ok) {
    if (quiet) console.error(result.text);
    else p.log.error(result.text);
    process.exitCode = 1;
    return;
  }

  if (quiet) {
    process.stdout.write(`${formatJsonOutput(result.text)}\n`);
    return;
  }

  const body =
    result.text.length > MAX_PRETTY_CHARS
      ? `${result.text.slice(0, MAX_PRETTY_CHARS)}\n…truncated. Re-run with --json for the whole response.`
      : result.text;
  p.note(body, target);
}

/**
 * Read one `--arg` value.
 *
 * JSON where JSON is unambiguous, text otherwise. A number is only taken as a
 * number when it round-trips exactly: an ICCID is 19 digits of significant
 * decimal, which a double silently rounds, and the tool would then look up a
 * subscriber nobody asked about.
 */
function parseArgValue(text: string): unknown {
  if (text === "true") return true;
  if (text === "false") return false;
  if (text === "null") return null;
  if (/^[[{"]/.test(text)) {
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }
  if (/^-?\d+(?:\.\d+)?$/.test(text)) {
    const n = Number(text);
    if (Number.isFinite(n) && String(n) === text) return n;
  }
  return text;
}

/**
 * Call one tool by name with raw arguments.
 *
 * Every registered tool has a generated command, but a command is only as good
 * as the schema it was built from, and a tool added since the last
 * `generate:domains` run has no command at all. This is the floor under that:
 * whatever the registry misses, `carrier tool <name>` still reaches.
 *
 * Nothing is validated beyond the key=value shape. The server rejects a bad
 * argument with a better message than the CLI could guess, and `dry_run` is not
 * injected — pass `--arg dry_run=true` to preview.
 */
export async function runRawTool(
  name: string,
  pairs: string[],
  opts: CapabilityFlags,
): Promise<void> {
  const quiet = opts.json === true;
  const args: Record<string, unknown> = {};
  const errors: string[] = [];

  for (const pair of pairs) {
    const eq = pair.indexOf("=");
    if (eq < 1) {
      errors.push(`--arg ${pair} is not key=value`);
      continue;
    }
    setArg(args, pair.slice(0, eq), parseArgValue(pair.slice(eq + 1)));
  }

  if (errors.length) {
    const message = withNextStep(`Cannot call \`${name}\`.`, [
      ...errors,
      "Arguments look like: --arg iccid=8931000000000000000 --arg limit=20",
    ]);
    if (quiet) console.error(message);
    else p.log.error(message);
    process.exitCode = 1;
    return;
  }

  const s = quiet ? null : p.spinner();
  s?.start(`Calling ${name}`);
  const result = await callMcpTool(name, args, {
    errorHints: [
      "List every tool this server registers: carrier env catalog",
      'Or describe the task in words: carrier ask "…"',
    ],
  });
  s?.stop(result.ok ? "Done" : "Failed");

  if (!result.ok) {
    if (quiet) console.error(result.text);
    else p.log.error(result.text);
    process.exitCode = 1;
    return;
  }

  if (quiet) {
    process.stdout.write(`${formatJsonOutput(result.text)}\n`);
    return;
  }

  const body =
    result.text.length > MAX_PRETTY_CHARS
      ? `${result.text.slice(0, MAX_PRETTY_CHARS)}\n…truncated. Re-run with --json for the whole response.`
      : result.text;
  p.note(body, name);
}
